// frontend/src/components/Chatbot/ChatInterface.tsx
// Parent orchestrator: owns all state, effects, and API calls.

import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type ForwardedRef,
} from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { MoreVertical } from 'lucide-react';
import { useChat } from '../../hooks/chat';
import { subscribeToAskChatbotHelp } from '../../utils/appEvents';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';

import ChatDisabledState, { type PendingHelpRequest } from './ChatDisabledState';
import SmartQuestionsSection from './SmartQuestionsSection';
import ChatMessages from './ChatMessages';
import PendingActionsPanel from './PendingActionsPanel';
import ChatInputBar from './ChatInputBar';

import { useStudentContext, getContextAwareQuestions } from '../../hooks/useStudentContext';
import { useDynamicQuestions } from '../../hooks/useDynamicQuestions';

import type { AcademicPlan } from '../../types';
import type { SuggestionsPayload } from '../../hooks/academicPlan/usePlanCore';
import { isGenEdProgram } from '@/utils/programClassification';

export interface ChatInterfaceHandle {
  setInputValue: (value: string, options?: { autoSend?: boolean }) => void;
  focusInput: () => void;
}

export interface ChatInterfaceProps {
  userId?: string | null;
  academicPlanId?: number | string | null;
  academicPlan?: (AcademicPlan & { id?: number | null }) | null;
  onExecuteCommand?: (commandString: string) => Promise<unknown> | void;
  onInputChange?: (value: string) => void;
  onPlanUpdate?: () => Promise<void>;
  onClearSuggestionsLocally?: () => void;
  onMergeSuggestionsLocally?: (payload: SuggestionsPayload) => void;
}

const ChatInterface = forwardRef<ChatInterfaceHandle, ChatInterfaceProps>(
  (
    {
      userId,
      academicPlanId,
      academicPlan,
      onInputChange,
      onPlanUpdate,
      onClearSuggestionsLocally,
      onMergeSuggestionsLocally,
    },
    ref: ForwardedRef<ChatInterfaceHandle>
  ) => {
    const { user } = useAuth();
    const { isAdmin } = useApp();
    const { isEnabled: chatbotEnabled, setEnabled: setChatbotEnabled } = useChatbot();

    const [input, setInput] = useState('');
    const [showSmartQuestions, setShowSmartQuestions] = useState(true);
    const [questionsOpacity, setQuestionsOpacity] = useState(1);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const [pendingHelpRequest, setPendingHelpRequest] = useState<PendingHelpRequest | null>(null);
    const optionsMenuRef = useRef<HTMLDivElement>(null);

    const handleDisable = async (): Promise<void> => {
      try {
        await setChatbotEnabled(false);
        setShowOptionsMenu(false);
      } catch (error) {
        console.error('Failed to disable chatbot:', error);
      }
    };

    const handleClearHistory = (): void => {
      clearChat();
      setShowSmartQuestions(true);
      setQuestionsOpacity(1);
      setShowOptionsMenu(false);
    };

    useClickOutside(optionsMenuRef, () => setShowOptionsMenu(false), showOptionsMenu);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const mountTime = useRef(Date.now());
    const userScrolledUp = useRef(false);
    const lastMessageCount = useRef(0);

    // AI context is loaded internally by useChat() below; calling useAIContext
    // here too triggered a second, redundant /ai-context fetch whose result was
    // discarded. Removed to halve the AI-context requests on chat open.
    const studentContext = useStudentContext(academicPlan as never);

    const { questions: dynamicQuestions, loading: questionsLoading } = useDynamicQuestions(
      academicPlanId
    );

    const {
      messages,
      isLoading,
      error,
      storeSession,
      clearChat,
      sendMessage,
      pendingActions,
      executedActions,
      blockedActions,
      confirmActions,
      rejectAllActions,
      isConfirming,
      isStreaming,
      cancelStreaming,
    } = useChat(
      userId ?? null,
      academicPlanId ?? null,
      user?.id ?? null,
      onPlanUpdate,
      isAdmin,
      onClearSuggestionsLocally,
      onMergeSuggestionsLocally
    );

    const staticQuestions = getContextAwareQuestions(studentContext.stage);
    const questions = dynamicQuestions.length > 0 ? dynamicQuestions : staticQuestions;

    const handleQuestionSelect = useCallback(
      (question: string): void => {
        if (question.trim() && !isLoading && !isConfirming) {
          sendMessage(question);
          setShowSmartQuestions(false);
        }
      },
      [isLoading, isConfirming, sendMessage]
    );

    useImperativeHandle(
      ref,
      () => ({
        setInputValue: (value: string, options: { autoSend?: boolean } = {}): void => {
          setInput(value);

          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
              const length = value.length;
              inputRef.current.setSelectionRange(length, length);
            }
          }, 100);

          if (options.autoSend && value.trim()) {
            setTimeout(() => {
              if (!isLoading) {
                sendMessage(value);
                setInput('');
              }
            }, 150);
          }
        },

        focusInput: (): void => {
          inputRef.current?.focus();
        },
      }),
      [isLoading, sendMessage]
    );

    const storeSessionRef = useRef(storeSession);
    storeSessionRef.current = storeSession;

    useEffect(() => {
      const currentMountTime = mountTime.current;
      return () => {
        const lifespan = Date.now() - currentMountTime;
        if (lifespan > 5000) {
          storeSessionRef.current();
        }
      };
    }, []);

    useEffect(() => {
      const isNewMessage = messages.length > lastMessageCount.current;
      lastMessageCount.current = messages.length;

      if (isNewMessage) {
        userScrolledUp.current = false;
      }

      if (!userScrolledUp.current && messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, [messages, isLoading]);

    useEffect(() => {
      if (messages.length > 0 && showSmartQuestions) {
        setQuestionsOpacity(0);
        const timer = setTimeout(() => {
          setShowSmartQuestions(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }, [messages.length, showSmartQuestions]);

    useEffect(() => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const handleScroll = (): void => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        userScrolledUp.current = distanceFromBottom > 100;
      };

      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => subscribeToAskChatbotHelp(({ message, action }) => {
      if (!chatbotEnabled) {
        setPendingHelpRequest({ message, action });
        return;
      }

      if (message && !isLoading && !isConfirming) {
        sendMessage(message, action);
        messagesContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }), [isLoading, isConfirming, sendMessage, chatbotEnabled]);

    useEffect(() => {
      if (chatbotEnabled && pendingHelpRequest?.message && !isLoading && !isConfirming) {
        const { message, action } = pendingHelpRequest;
        const timer = setTimeout(() => {
          sendMessage(message, action);
          setPendingHelpRequest(null);
          setShowSmartQuestions(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    }, [chatbotEnabled, pendingHelpRequest, isLoading, isConfirming, sendMessage]);

    // First visit to a plan with nothing declared: the empty chat greets the
    // student in the assistant's voice, locally. It used to SEND "Hi! I'm new
    // here…" as the student — a message they never typed, plus a Claude turn
    // they never asked for. Now nothing is sent until the student asks.
    //
    // Gated on the plan matching its id: the id resolves before the plan
    // detail GET returns, and in that window the plan is the empty default,
    // which reads as "declared nothing" for every student.
    const onboardingWelcome = useMemo((): string | null => {
      if (!academicPlanId || !academicPlan?.id) return null;
      if (String(academicPlan.id) !== String(academicPlanId)) return null;

      const hasRealMajor = academicPlan.majors?.some((m) => !isGenEdProgram(m));
      const hasMinors = !!academicPlan.minors && academicPlan.minors.length > 0;
      if (!hasRealMajor && !hasMinors) {
        return (
          "Welcome! You haven't added a major yet. I can help you choose one " +
          'and set up your semesters. Pick a question below or ask me anything.'
        );
      }

      const hasSemesters = Object.keys(academicPlan.semesters || {}).some(
        (key) => !['000008', '000009', '-1'].includes(key)
      );
      if (!hasSemesters) {
        return (
          "Welcome! Your major is set. Next, let's add your semesters. " +
          'Pick a question below or ask me anything.'
        );
      }
      return null;
    }, [academicPlan, academicPlanId]);

    const handleInputChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>): void => {
        const value = e.target.value;
        setInput(value);
        if (onInputChange) {
          onInputChange(value);
        }
      },
      [onInputChange]
    );

    const handleSubmit = (e: FormEvent | KeyboardEvent<HTMLTextAreaElement>): void => {
      e.preventDefault();
      if (input.trim() && !isLoading && !isConfirming) {
        sendMessage(input);
        setInput('');
        setShowSmartQuestions(false);
      }
    };

    if (!chatbotEnabled) {
      return (
        <ChatDisabledState
          pendingHelpRequest={pendingHelpRequest}
          onClearPendingRequest={() => setPendingHelpRequest(null)}
        />
      );
    }

    return (
      <div className="h-full flex flex-col">
        {/* UI Tier 3.3b: unboxed column — header on the ground, bubbles as
            cards, input pinned at the bottom (one framing language). */}
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between px-1 pb-3">
            <h3 className="text-lg font-semibold text-ink">AI Assistant</h3>
            <div className="relative" ref={optionsMenuRef}>
              <button
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                title="Options"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {showOptionsMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-surface-card rounded-card shadow-lg border border-line-soft py-1 z-50">
                  <button
                    onClick={handleClearHistory}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-surface-sunken"
                  >
                    Clear chat history
                  </button>
                  <button
                    onClick={handleDisable}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-surface-sunken"
                  >
                    Disable AI Assistant
                  </button>
                </div>
              )}
            </div>
          </div>

          {showSmartQuestions && (
            <div
              className="mb-4 transition-opacity duration-300"
              style={{ opacity: questionsOpacity }}
            >
              <SmartQuestionsSection
                questions={questions}
                onQuestionSelect={handleQuestionSelect}
                isEmptyState={false}
                loading={questionsLoading}
              />
            </div>
          )}

          <ChatMessages
            messages={messages as never}
            showSmartQuestions={showSmartQuestions}
            welcomeMessage={onboardingWelcome}
            isLoading={isLoading}
            isStreaming={isStreaming}
            cancelStreaming={cancelStreaming}
            error={error}
            messagesContainerRef={messagesContainerRef}
            messagesEndRef={messagesEndRef}
          />

          <PendingActionsPanel
            pendingActions={pendingActions as never}
            executedActions={executedActions as never}
            blockedActions={blockedActions as never}
            onConfirm={confirmActions as never}
            onReject={rejectAllActions as never}
            isConfirming={isConfirming}
          />

          <ChatInputBar
            ref={inputRef}
            input={input}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isConfirming={isConfirming}
          />
        </div>
      </div>
    );
  }
);

ChatInterface.displayName = 'ChatInterface';

export default ChatInterface;
